import { Trash, CirclePlus } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectIcon } from '@/components/common/Icon';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const FormGeneral = () => {
  const form = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'activityQualifications'
  });

  return (
    <>
      {/* ----- GENERAL DETAILS ----- */}
      <div className="mb-8">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="flex-auto">
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Select Icon</FormLabel>
                <FormControl>
                  <SelectIcon value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="feePercentage"
            render={({ field }) => (
              <FormItem className="flex-auto">
                <FormLabel>ProFound Fee</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                  />
                </FormControl>
                <FormDescription>
                  This is a percentage that ProFound will charge on top of the
                  price the coach sets. This is in addition to fees on the fee
                  page.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isEnabled"
            render={({ field }) => (
              <FormItem className="flex-auto">
                <div className="flex items-center space-x-2 pt-10 gap-2 mb-4">
                  <FormLabel>Enabled</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </div>
                <FormDescription>
                  This will enable or disable the activity from being shown on
                  the home page.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <Separator />

      {/* ----- REQUIREMENTS ----- */}
      <div className="mb-8">
        <h3 className="text-lg mb-6 font-bold">Requirements</h3>

        <div className="mb-6">
          <h4>Application Steps</h4>
          <span className="text-sm text-muted-foreground mb-4 block">
            These checkboxes will control what forms will show up on the coach
            application for this activity
          </span>
          <div className="space-y-2">
            <FormField
              control={form.control}
              name="isWaiverRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-2 leading-none">
                    <FormLabel>Waiver Required</FormLabel>
                    <FormDescription>
                      This will require the coach to sign a waiver for this
                      activity
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isFirstAidRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-2 leading-none">
                    <FormLabel>First Aid Required</FormLabel>
                    <FormDescription>
                      This will require the coach to have first aid
                      certification for this activity
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isQualificationRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-2 leading-none">
                    <FormLabel>Qualification Required</FormLabel>
                    <FormDescription>
                      This will require the coach to have specific
                      qualifications for this activity
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isLocationRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-2 leading-none">
                    <FormLabel>Location Required</FormLabel>
                    <FormDescription>
                      This will require a designated location for this activity
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>
        </div>
        <h4>Qualifications</h4>
        <span className="text-sm text-muted-foreground mb-4 block">
          Setup the qualifications that are required for this activity. This
          will show on the contract and in the qualifications application step.
        </span>
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-center space-x-2 mb-2">
            <div className="w-4/12">
              <FormField
                control={form.control}
                name={`activityQualifications.${index}.name`}
                render={({ field }) => (
                  <FormItem className="flex-auto">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-8/12">
              <FormField
                control={form.control}
                name={`activityQualifications.${index}.description`}
                render={({ field }) => (
                  <FormItem className="flex-auto">
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="pt-8">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(index)}
                className="flex-shrink-0"
              >
                <Trash />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            append({ name: '', description: '', sortOrder: fields.length + 1 })
          }
        >
          <CirclePlus />
          Add Qualification
        </Button>
      </div>

      <Separator />

      {/* ----- CONTRACT SPECIFICS ----- */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-4">Contract Specifics</h3>
        <FormField
          control={form.control}
          name="servicesContractStr"
          render={({ field }) => (
            <FormItem className="flex-auto">
              <FormLabel>Services Contract String</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>
                This is what will show up on the contract for the description of
                services
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

export default FormGeneral;
